import DataGridStriped, { DataGridToolbar } from '@components/datagrid';
import { useMediaQuery, useTheme } from '@mui/material';
import strings from '@src/app/strings/LocalisedStrings';
import { dateFormatter, numberFormatterDollar, numberFormatterHoldings, numberFormatterWithoutSymbols } from '@src/app/utils/GenericUtils';
import { TransactionsForAccountsViewModel } from 'clientportalshared';
import { FC, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '@src/app/hooks/store';
import { setTransactionsFilter } from '@src/app/store/reducers/transactions';
import CustomGridFilterPanel from '@components/datagrid/CustomGridFilterPanel';
import { getGridStringOperators, GridColumns, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { dateBetweenOperator, valueBetweenOperator } from '@components/datagrid/filterOperators';
import DataGridBox from '@components/datagrid/DataGridBox';

export interface TransactionsDataGridProps {
  data: TransactionsForAccountsViewModel[];
  isLoading?: boolean;
  filterIds?: number[];
  filteredItem?: string;
  hideSecurity?: boolean;
  clearFilteringButton?: boolean;
  currentPageSize: number;
  setCurrentPageSize: (pageSize: number) => void;
}

const getTransactionsGridColumns = (props: TransactionsDataGridProps) => {
  const columns: GridColumns = [];

  if (!props.hideSecurity) {
    columns.push({
      field: 'securityCode',
      headerName: strings.code,
      flex: 1,
      minWidth: 100, // was 170
    });
    columns.push({
      field: 'securityName',
      headerName: strings.security,
      flex: 1,
      minWidth: 140, // was 200
    });
  }

  const staticColumns: GridColumns = [
    {
      field: 'tradeDate',
      headerName: strings.date,
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => dateFormatter(params.value?.toString() || ''),
      filterOperators: [...getGridStringOperators(), dateBetweenOperator],
    },
    {
      field: 'comments',
      headerName: strings.description,
      flex: 2,
      minWidth: 160,
      valueGetter: (params) => (params.value ?? '').replace(/\t/g, ' '),
    },
    {
      field: 'systemTransactionType',
      headerName: strings.transactionType,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'units',
      headerName: strings.quantity,
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0, 4),
      valueFormatter: (params) => numberFormatterHoldings(Number(params.value || 0), 4),
      filterOperators: [...getGridStringOperators(), valueBetweenOperator],
    },
    {
      field: 'unitPrice',
      headerName: strings.price,
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0, 2),
      valueFormatter: (params) => numberFormatterDollar(Number(params.value || 0), 2),
      filterOperators: [...getGridStringOperators(), valueBetweenOperator],
    },
    {
      field: 'transactionValue',
      headerName: strings.value,
      flex: 1,
      minWidth: 90,
      valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0, 2),
      valueFormatter: (params) => numberFormatterDollar(Number(params.value || 0), 2),
      filterOperators: [...getGridStringOperators(), valueBetweenOperator],
    },
  ];

  columns.push(...staticColumns);
  return columns;
};

const TransactionsDataGrid: FC<TransactionsDataGridProps> = (props: TransactionsDataGridProps): JSX.Element => {
  const [search, setSearch] = useState<string>('');
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.between('xs', 'lg'));
  const isLessThanHD = useMediaQuery(theme.breakpoints.down(1280));
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>({});
  const [updatedColumns, setUpdatedColumns] = useState<boolean>(false);

  const rows = useMemo(() => {
    let transactions = props.data;
    if (props.filteredItem && props.filteredItem.length > 0 && props.filterIds) {
      transactions = transactions.filter((transaction) => transaction.id && props.filterIds?.includes(transaction.id));
    }
    if (props.hideSecurity) {
      return transactions.filter((value) => value.tradeDate && dateFormatter(value.tradeDate || '').includes(search.toLowerCase()));
    }
    return transactions.filter(
      (value) =>
        (value.securityCode && value.securityCode.toLowerCase().includes(search.toLowerCase())) ||
        (value.securityName && value.securityName.toLowerCase().includes(search.toLowerCase()))
    );
  }, [props.data, props.filterIds, search]);

  useEffect(() => {
    if (isLessThanHD) {
      if (!updatedColumns) {
        setColumnVisibility({
          comments: false,
        });
        setUpdatedColumns(true);
      }
    } else {
      setUpdatedColumns(false);
    }
  }, [isLessThanHD]);

  return (
    <DataGridBox>
      <DataGridStriped
        getRowId={(row) => row.id}
        columns={getTransactionsGridColumns(props)}
        rows={rows}
        loading={props.isLoading}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={(model: GridColumnVisibilityModel) => {
          setColumnVisibility(model);
        }}
        initialState={{
          pinnedColumns: {
            left: props.hideSecurity ? ['tradeDate'] : ['securityCode'],
          },
          pagination: {
            pageSize: small ? 10 : 25,
          },
        }}
        pageSize={small ? 10 : props.currentPageSize}
        onPageSizeChange={(pageSize: number) => props.setCurrentPageSize(pageSize)}
        componentsProps={{
          toolbar: {
            csvFileName: `Transactions-${dateFormatter(new Date())}`,
            searchValue: search,
            searchPlaceholder: props.hideSecurity ? 'Search by Date' : 'Search by Code or Security...',
            setSearchValue: setSearch,
            onClearFilters: props.clearFilteringButton
              ? () => {
                  dispatch(setTransactionsFilter({ name: '', ids: [] }));
                }
              : null,
          },
        }}
        localeText={{ noRowsLabel: strings.emptyPortfolioDataMessage }}
        pagination
        rowsPerPageOptions={small ? [10] : [25, 50, 100]}
        components={{
          Toolbar: DataGridToolbar,
          FilterPanel: CustomGridFilterPanel,
        }}
      />
    </DataGridBox>
  );
};

export default TransactionsDataGrid;
